import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Spin } from "antd";
import HInput from "../../custom/input/input/Input";
import "./InboxFilterDropdown.css";

const SCROLL_THRESHOLD = 20;
const SEARCH_DEBOUNCE_MS = 300;

const InboxFilterDropdown = ({
    value,
    savedFiltersList = [],
    searchResults = null,
    isSearching = false,
    onSelectSavedFilter,
    onEditFilter,
    onDeleteFilter,
    onLoadMore,
    onSearch,
    hasMore = false,
    dropdownOpen,
    onDropdownOpenChange,
    children,
}) => {
    const [searchText, setSearchText] = useState("");
    const [isLoadingMoreFilters, setIsLoadingMoreFilters] = useState(false);
    const userListRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const filters = useMemo(() => (Array.isArray(savedFiltersList) ? savedFiltersList : []), [savedFiltersList]);
    const searchList = useMemo(() => (Array.isArray(searchResults) ? searchResults : []), [searchResults]);
    const isActiveSearch = searchText?.trim()?.length > 0;

    useEffect(() => {
        if (!dropdownOpen) {
            setSearchText("");
            onSearch?.("");
        }
    }, [dropdownOpen, onSearch]);

    const handleSearchChange = useCallback(
        (e) => {
            const value = e?.target?.value ?? "";
            setSearchText(value);
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                onSearch?.(value.trim());
            }, SEARCH_DEBOUNCE_MS);
        },
        [onSearch]
    );

    useEffect(() => {
        return () => clearTimeout(debounceTimerRef.current);
    }, []);

    const handleUserListScroll = useCallback(async () => {
        const container = userListRef.current;
        if (!container || !hasMore || isLoadingMoreFilters) return;
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD) {
            setIsLoadingMoreFilters(true);
            try {
                await onLoadMore?.();
            } finally {
                setIsLoadingMoreFilters(false);
            }
        }
    }, [hasMore, isLoadingMoreFilters, onLoadMore]);

    const userFiltersToDisplay = useMemo(() => {
        if (isActiveSearch) {
            return searchList.filter((f) => !f.systemDefined);
        }
        return filters.filter((f) => !f.systemDefined);
    }, [filters, searchList, isActiveSearch]);

    const systemDefinedFilters = useMemo(() => {
        return filters
            .filter((f) => f.systemDefined)
            .sort((a, b) => new Date(a.createdTime ?? 0).getTime() - new Date(b.createdTime ?? 0).getTime());
    }, [filters]);

    const displayLabel = useMemo(() => {
        const matched = filters.find((f) => f.filterId === value);
        return matched?.filterName ?? "All Conversations";
    }, [value, filters]);

    const handleSelectSaved = (filterId) => {
        onSelectSavedFilter(filterId);
        onDropdownOpenChange?.(false);
    };

    const handleEdit = (e, filter) => {
        e.stopPropagation();
        onDropdownOpenChange?.(false);
        onEditFilter?.(filter);
    };

    const handleDelete = (e, filter) => {
        e.stopPropagation();
        onDropdownOpenChange?.(false);
        onDeleteFilter?.(filter);
    };

    const dropdownContent = (
        <div className="inbox-filter-dropdown-menu hs-bg-white hs-border-8 overflow-auto py-2">
            <div className="inbox-filter-dropdown-section">
                {systemDefinedFilters?.map((f) => (
                    <div
                        key={f.filterId}
                        data-cy={`inbox-filter-view-${(f.filterName ?? "").toLowerCase().replace(/\s+/g, "-")}`}
                        className={`d-flex align-items-center justify-content-between px-3 py-2 cursor-pointer hs-border-6 hs-fs-14 inbox-filter-dropdown-item ${value === f.filterId ? "hs-bg-light-violet hs-color-violet" : ""}`}
                        onClick={() => handleSelectSaved(f.filterId)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleSelectSaved(f.filterId);
                            }
                        }}
                    >
                        <span>{f.filterName}</span>
                    </div>
                ))}
            </div>
            <div>
                <div onClick={(e) => e.stopPropagation()} className="hs-w-100">
                    <HInput
                        customClasses="border-none hs-w-100"
                        placeholder="Search for saved filters"
                        value={searchText}
                        onChange={handleSearchChange}
                        className="selectBoxSearchInput"
                        floatingLabel={false}
                        variant="underlined"
                    />
                </div>
            </div>
            <div className="inbox-filter-dropdown-section">
                <div className="inbox-filter-dropdown-user-list overflow-auto" ref={userListRef} onScroll={handleUserListScroll}>
                    {isSearching ? (
                        <div className="d-flex justify-content-center py-2">
                            <Spin size="small" />
                        </div>
                    ) : userFiltersToDisplay.length === 0 ? (
                        <div className="p-3 hs-fs-13 hs-text-secondary text-center">
                            {isActiveSearch ? "No matching filters" : "No saved filters"}
                        </div>
                    ) : (
                        userFiltersToDisplay.map((filter) => (
                            <div
                                key={filter.filterId}
                                role="button"
                                tabIndex={0}
                                className={`d-flex align-items-center justify-content-between px-3 py-2 cursor-pointer hs-border-6 hs-fs-14 inbox-filter-dropdown-item inbox-filter-dropdown-item-user ${value === filter.filterId ? "hs-bg-light-violet hs-color-violet" : ""}`}
                                onClick={() => handleSelectSaved(filter.filterId)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleSelectSaved(filter.filterId);
                                    }
                                }}
                            >
                                <span className="flex-grow-1 hs-text-truncate min-w-0">{filter.filterName}</span>
                                <span className="d-flex align-items-center gap-2 inbox-filter-dropdown-item-actions">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className="actionIconsSprite hs-edit-icon inbox-filter-dropdown-action-icon"
                                        title="Edit filter"
                                        onClick={(e) => handleEdit(e, filter)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleEdit(e, filter);
                                            }
                                        }}
                                    />
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className="actionIconsSprite hs-delete-icon-small inbox-filter-dropdown-action-icon"
                                        title="Delete filter"
                                        onClick={(e) => handleDelete(e, filter)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleDelete(e, filter);
                                            }
                                        }}
                                    />
                                </span>
                            </div>
                        ))
                    )}
                    {isLoadingMoreFilters && (
                        <div className="d-flex justify-content-center py-2">
                            <Spin size="small" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <Dropdown
            open={dropdownOpen}
            onOpenChange={onDropdownOpenChange}
            dropdownRender={() => dropdownContent}
            trigger={["click"]}
            placement="bottomLeft"
            getPopupContainer={(node) => node?.parentNode ?? document.body}
        >
            {children ?? (
                <div
                    data-cy="inbox-filter-view-dropdown-trigger"
                    className="d-flex align-items-center cursor-pointer hs-fs-15 hs-fw-600 py-1 w-100 min-w-0 inbox-filter-dropdown-trigger"
                    role="button"
                    tabIndex={0}
                    title={displayLabel}
                >
                    <span className="hs-text-truncate min-w-0">{displayLabel}</span>
                    <div className="actionIconsSprite customComponentDropDownIcon-small flex-shrink-0 ms-1" />
                </div>
            )}
        </Dropdown>
    );
};

export default InboxFilterDropdown;
